'use client';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ShopEmptyState } from '@/app/[locale]/shop/[username]/(shop)/components/EmptyState';
import { SellingProductsEmptyState } from '@/app/[locale]/shop/[username]/(shop)/components/SellingProductsEmptyState';
import { SimilarToSold } from '@/app/[locale]/shop/[username]/(shop)/components/SimilarToSold';
import { getActiveFiltersCount } from '@/modules/search/helpers';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { useIsViewingOwnShop } from '@/modules/shop/hooks/useIsViewingOwnShop';

type Props = {
  soldProductIds?: number[];
  isSoldOut?: boolean;
};

export function ShopProductsEmptyStateVariant({
  soldProductIds = [],
  isSoldOut = false,
}: Props) {
  const t = useTranslations('shop');
  const { username } = useParams<{ username: string; locale: string }>();
  const { searchFilters } = useSearchFilters();
  const activeFiltersCount = getActiveFiltersCount(searchFilters);
  const isViewingOwnShop = useIsViewingOwnShop();
  const hasFiltersApplied = activeFiltersCount > 0;

  function renderEmptyStateMessage() {
    if (isViewingOwnShop) {
      return <SellingProductsEmptyState />;
    }
    if (hasFiltersApplied) {
      return (
        <ShopEmptyState className={styles.noResultsContainer}>
          <Body variant="sm-bold" className={styles.noResultsFound}>
            {t('NoResults1')}
          </Body>
          <Body variant="sm-regular">{t('NoResults2')}</Body>
        </ShopEmptyState>
      );
    }

    const DefaultEmptyState = isSoldOut ? (
      <ShopEmptyState>
        <Body variant="sm-regular">{t('SoldOut', { username })}</Body>
      </ShopEmptyState>
    ) : (
      <ShopEmptyState>
        <Body variant="sm-regular">{t('NoProducts', { username })}</Body>
      </ShopEmptyState>
    );

    return DefaultEmptyState;
  }

  return (
    <>
      {renderEmptyStateMessage()}
      <SimilarToSold soldProductIds={soldProductIds || []} />
    </>
  );
}
